import React, { useRef } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import styled from "styled-components/macro";

import ClimateMonthlyChart from "./ClimateMonthlyChart";
import SaveGraphButton from "../../graphs/SaveGraphButton";
import useClimateMonthlySeries, {
  NORMAL_KEY,
} from "../../../hooks/charts/useClimateMonthlySeries";
import {
  COMPARE_YEARS_SHOWN,
  MAX_COMPARE_YEARS,
  MHFD_PRECIP_CAVEAT,
  STATIONS,
  UNIT_OPTIONS,
} from "../../../pages/climateMonthly/climateCopy";

/* Matches the metrics RawTable uses, so the chart's data table and the raw
   data table below it read as one thing. */
const TableWrapper = styled(TableContainer)`
  width: 100%;
  max-height: 400px;
  overflow: auto;

  .MuiTable-root {
    font-size: 0.75rem;
  }

  .MuiTableCell-root {
    padding: 4px 8px;
    font-size: 0.75rem;
    white-space: nowrap;
  }

  .MuiTableCell-head {
    font-weight: bold;
    background: #fff;
  }
`;

const formatCell = (value, decimals) =>
  value == null ? "—" : value.toFixed(decimals);

const csvEscape = (value) => {
  const text = value == null ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Chart, its display toggles and its data table, for the Monthly Climate
 * Comparison page and its embed. Both render this so the two cannot drift
 * apart.
 */
export default function ClimateMonthlyChartContainer({
  variable = "precip",
  waterYear,
  compareYears = [],
  normalStart,
  showKapa = true,
  showMhfd = true,
  units = "f",
  height = 400,
  allWaterYears = [],
  mhfdDisabled = false,
  onCompareYearsChange,
  onUnitsChange,
  onShowKapaChange,
  onShowMhfdChange,
  showControls = true,
  showTable = true,
}) {
  const chartRef = useRef();
  const {
    series,
    seriesKeys,
    cellMeta,
    incompleteNotes,
    totals,
    normalLabel,
    normalYears,
    unit,
    unitLong,
    axisLabel,
    variableConfig: config,
    isLoading,
    isError,
  } = useClimateMonthlySeries({
    variable,
    waterYear,
    compareYears,
    normalStart,
    showKapa,
    showMhfd,
    units,
  });

  const decimals = config.decimals;

  const hasValues = series.some((row) =>
    seriesKeys.some((entry) => row[entry.key] != null)
  );

  /* A one-sentence summary of what the bars show, read out in place of the
     canvas by a screen reader. */
  const ariaLabel = [
    `Bar chart of ${config.label.toLowerCase()} by water-year month,`,
    `October through September, in ${unitLong},`,
    `with the long-term average drawn as a line across the months.`,
    `Series: ${seriesKeys.map((entry) => entry.label).join("; ")}.`,
  ].join(" ");

  /* The newest COMPARE_YEARS_SHOWN years, excluding the one already charted,
     plus any year a shared URL has selected from further back so it can still
     be switched off. */
  const compareChipYears = (() => {
    const offered = allWaterYears.filter((year) => year !== waterYear);
    const recent = offered.slice(0, COMPARE_YEARS_SHOWN);
    const pinned = offered.filter(
      (year) => compareYears.includes(year) && !recent.includes(year)
    );
    return [...recent, ...pinned].sort((a, b) => b - a);
  })();

  const toggleCompareYear = (year) => {
    const next = compareYears.includes(year)
      ? compareYears.filter((y) => y !== year)
      : [...compareYears, year].slice(-MAX_COMPARE_YEARS);
    onCompareYearsChange?.(next);
  };

  const handleCsv = () => {
    const header = ["Month", ...seriesKeys.map((entry) => entry.label)];
    const rows = series.map((row) => [
      row.month,
      ...seriesKeys.map((entry) =>
        row[entry.key] == null ? "" : row[entry.key].toFixed(decimals)
      ),
    ]);

    if (config.hasWaterYearTotal) {
      rows.push([
        "Water year total",
        ...seriesKeys.map((entry) =>
          totals[entry.key] == null ? "" : totals[entry.key].toFixed(decimals)
        ),
      ]);
    }

    const csv = [
      [`${config.label} by water-year month (${unit})`],
      [normalLabel],
      [],
      header,
      ...rows,
    ]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");

    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `monthly-climate-${variable}-wy${waterYear}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /* The display toggles. Comparison years and the second station add or remove
     a series, so they are chips; units picks between named alternatives, so it
     is a select, the way the y-axis scale type is on Reservoir Inflow
     Comparisons. */
  const additionalFilters = (
    <Box
      component="fieldset"
      mb={2}
      p={2}
      borderColor="grey.300"
      borderRadius={4}
      bgcolor="#f9f9f9"
    >
      <Typography component="legend" variant="subtitle2" gutterBottom>
        Additional Graph Filters
      </Typography>

      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="flex-start"
        flexDirection="column"
        style={{ gap: 16 }}
      >
        {config.isTemperature && (
          <Box display="flex" flexDirection="column" minWidth={200}>
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Temperature Units
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={units}
                onChange={(event) => onUnitsChange?.(event.target.value)}
                inputProps={{ "aria-label": "Temperature units" }}
              >
                {UNIT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Box display="flex" flexDirection="column">
          <Typography variant="caption" color="textSecondary" gutterBottom>
            Select Water Years to Compare (up to {MAX_COMPARE_YEARS})
          </Typography>
          <Box display="flex" flexWrap="wrap" style={{ gap: 2 }}>
            {compareChipYears.map((year) => (
              <Chip
                key={year}
                label={year}
                clickable
                color={compareYears.includes(year) ? "primary" : "default"}
                onClick={() => toggleCompareYear(year)}
                style={{ width: 60 }}
                role="checkbox"
                aria-checked={compareYears.includes(year)}
                aria-label={`Toggle water year ${year}`}
              />
            ))}
          </Box>
        </Box>

        <Box display="flex" flexDirection="column">
          <Typography variant="caption" color="textSecondary" gutterBottom>
            Stations
          </Typography>
          <Box display="flex" flexWrap="wrap" style={{ gap: 2 }}>
            <Tooltip
              title={`Show or hide the ${STATIONS.KAPA.longLabel}. The long-term average is built from this station and stays on the chart either way.`}
              arrow
            >
              <Chip
                label={STATIONS.KAPA.label}
                clickable
                color={showKapa ? "primary" : "default"}
                onClick={() => onShowKapaChange?.(!showKapa)}
                role="checkbox"
                aria-checked={showKapa}
                aria-label={`Toggle ${STATIONS.KAPA.longLabel}`}
              />
            </Tooltip>
            <Tooltip
              title={
                mhfdDisabled
                  ? `The ${STATIONS.MHFD.longLabel} record starts in ${STATIONS.MHFD.recordStarts}, so it has no data for the selected water years.`
                  : `Show or hide the ${STATIONS.MHFD.longLabel}`
              }
              arrow
            >
              {/* A disabled control does not fire the events a tooltip needs,
                  so the span carries them instead. */}
              <span>
                <Chip
                  label={STATIONS.MHFD.label}
                  clickable={!mhfdDisabled}
                  disabled={mhfdDisabled}
                  color={showMhfd && !mhfdDisabled ? "primary" : "default"}
                  onClick={
                    mhfdDisabled
                      ? undefined
                      : () => onShowMhfdChange?.(!showMhfd)
                  }
                  role="checkbox"
                  aria-checked={showMhfd && !mhfdDisabled}
                  aria-label={`Toggle ${STATIONS.MHFD.longLabel}`}
                />
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  if (isLoading)
    return (
      <Box
        height={height}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );

  if (isError)
    return (
      <Box
        height={height}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        Could not load the climate data. Please try again.
      </Box>
    );

  if (!hasValues)
    return (
      <>
        <Box
          height={height}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          No data for current selections
        </Box>
        {showControls && additionalFilters}
      </>
    );

  return (
    <>
      {showControls && additionalFilters}

      <Box display="flex" justifyContent="flex-end" alignItems="center">
        <Button
          size="small"
          startIcon={<GetAppIcon />}
          onClick={handleCsv}
          aria-label="Download these figures as CSV"
        >
          CSV
        </Button>
        <SaveGraphButton
          ref={chartRef}
          title={`Monthly Climate Comparison ${config.shortLabel} WY${waterYear}`}
        />
      </Box>

      <ClimateMonthlyChart
        ref={chartRef}
        series={series}
        seriesKeys={seriesKeys}
        cellMeta={cellMeta}
        unit={unit}
        axisLabel={axisLabel}
        decimals={decimals}
        normalLabel={normalLabel}
        normalYears={normalYears}
        height={height}
        ariaLabel={ariaLabel}
      />

      {incompleteNotes.length > 0 && (
        <Box mt={2}>
          <Typography variant="caption" color="textSecondary" component="p">
            Faded bars are months with fewer days of data than the calendar
            month holds, either still in progress or with a gap in the record:{" "}
            {incompleteNotes.map((note) => note.text).join("; ")}.
          </Typography>
        </Box>
      )}

      {showMhfd && (
        <Box mt={1}>
          <Typography variant="caption" color="textSecondary" component="p">
            The {STATIONS.MHFD.longLabel} record starts in{" "}
            {STATIONS.MHFD.recordStarts}; months before that are blank rather
            than zero.
          </Typography>
          {/* Precipitation only. The two stations' temperatures agree to
              within about 1.7 degrees, so the caveat does not apply there. */}
          {!config.isTemperature && (
            <Typography variant="caption" color="textSecondary" component="p">
              {MHFD_PRECIP_CAVEAT}
            </Typography>
          )}
        </Box>
      )}

      {showTable && (
        <Box mt={4}>
          <Typography variant="h6" component="h3" gutterBottom>
            Chart Data
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginBottom: 8 }}
          >
            The same figures the chart plots, in {unitLong}. This is the
            accessible equivalent of the graph above.
          </Typography>
          <TableWrapper>
            <Table size="small" stickyHeader aria-label={ariaLabel}>
              <TableHead>
                <TableRow>
                  <TableCell component="th" scope="col">
                    Month
                  </TableCell>
                  {seriesKeys.map((entry) => (
                    <TableCell
                      key={entry.key}
                      component="th"
                      scope="col"
                      align="right"
                    >
                      {entry.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {series.map((row) => (
                  <TableRow key={row.wy_month_order}>
                    <TableCell component="th" scope="row">
                      {row.month}
                    </TableCell>
                    {seriesKeys.map((entry) => {
                      const meta =
                        cellMeta[`${entry.key}|${row.wy_month_order}`];
                      const incomplete =
                        entry.key !== NORMAL_KEY && meta?.incomplete;
                      return (
                        <TableCell
                          key={entry.key}
                          align="right"
                          title={
                            incomplete
                              ? `Incomplete: ${meta.daysWithData} of ${meta.daysInMonth} days of data`
                              : undefined
                          }
                        >
                          {formatCell(row[entry.key], decimals)}
                          {incomplete && (
                            <>
                              {" "}
                              <span
                                aria-label="incomplete month"
                                title="Incomplete month"
                              >
                                *
                              </span>
                            </>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
                {config.hasWaterYearTotal && (
                  <TableRow>
                    <TableCell component="th" scope="row">
                      <strong>Water year total</strong>
                    </TableCell>
                    {seriesKeys.map((entry) => (
                      <TableCell key={entry.key} align="right">
                        <strong>
                          {formatCell(totals[entry.key], decimals)}
                        </strong>
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableWrapper>
          {config.hasWaterYearTotal && (
            <Typography variant="caption" color="textSecondary" component="p">
              A water year total is shown only where all twelve months are
              present. An asterisk marks a month with fewer days of data than
              the calendar month holds.
            </Typography>
          )}
        </Box>
      )}
    </>
  );
}
